<template>
    <!-- Mobile View for small screens -->
    <MobileView v-if="isMobile && dataLoaded && !hasError"
                :data="data"
                :navItems="navItems"
                :techSkills="tech"
                :activeNav="activeNav"
                :hasError="hasError"
                :errorType="errorType"
                @nav-change="setActiveNav"
                @reset-error="resetError" />

    <!-- Desktop View for larger screens -->
    <main v-else :class="{ 'sidebar-collapsed': isSidebarCollapsed }">
        <!-- Show Sidebar for all sections -->
        <Sidebar v-if="dataLoaded && !hasError" :profileData="data" :showContacts="showContacts"
            :toggleContacts="toggleContacts" @sidebar-state="updateSidebarState" />

        <div class="main-content" :class="{ 
            'main-expanded': isSidebarCollapsed, 
            'main-with-sidebar': !isSidebarCollapsed 
        }">
            <nav v-if="!hasError" class="navbar">
                <ul class="navbar-list">
                    <li class="navbar-item" v-for="item in visibleNavItems" :key="item.name">
                        <button class="navbar-link" :class="{ active: activeNav === item.name }"
                            @click="setActiveNav(item.name)">
                            {{ item.name }}
                        </button>
                    </li>
                </ul>
            </nav>

            <!-- Error component display -->
            <ErrorFallback v-if="hasError" :errorType="errorType" @go-home="resetError" />

            <!-- Main content components -->
            <PlayGround v-else-if="activeNav === 'Playground'" :projectData="data.projects" />
            <About v-else-if="activeNav === 'About'" 
                :aboutText="data.about" 
                :services="data.generalSkill"
                :techSkills="tech" 
                @mounted="logComponentData('About', {generalSkill: data.generalSkill})" />
            <ProfessionalHistory v-else-if="activeNav === 'Professional History'" :experienceData="data.experience"
                :educationData="data.education" />
            <SeoTester v-else-if="activeNav === 'SEO Test'" />
        </div>
    </main>
</template>


<script>
// Vue framework imports
import { useHead } from '@vueuse/head';

// Layout components
import Sidebar from './SidebarVal.vue';
import MobileView from './MobileView.vue';

// Page components
import About from '../pages/AboutMe.vue';
import ProfessionalHistory from '../pages/ProfessionalHistory.vue';
import PlayGround from '../pages/PlayGround.vue';

// Common components
import ErrorFallback from '../common/ErrorFallback.vue';

// Dev tools
import SeoTester from '../dev-tools/SeoTester.vue';

// Utilities
import { routeSeoSettings, generateMetaInfo } from '../../utils/seo';

export default {
    components: {
        Sidebar,
        About,
        ProfessionalHistory,
        PlayGround,
        SeoTester,
        ErrorFallback,
        MobileView
    },
    setup() {
        return {};
    },
    computed: {
        visibleNavItems() {
            // Always filter out the SEO Test item from navigation
            return this.navItems.filter(item => 
                item.name !== 'SEO Test'
            );
        }
    },
    data() {
        return {
            // Get the sidebar state from localStorage if available, default to collapsed
            isSidebarCollapsed: localStorage.getItem('sidebarState') !== 'expanded',
            isMobile: this.checkIfMobile(), // Mobile detection
            data: {}, // Will hold profile data
            navItems: [], // Will hold navigation items from settings
            showContacts: true,
            activeNav: 'About', // Default active navigation section
            dataLoaded: false, // Flag to indicate data loading state
            pageLoaded: true, // Start with true to avoid hiding content
            showModal: false,
            modalImage: '',
            modalTitle: '',
            modalText: '',
            tech: [],
            hasError: false,
            errorType: 'not-found' // Options: 'not-found', 'server-error'
        };
    },
    mounted() {
        // Show content immediately after mounting
        this.pageLoaded = true;
        
        // Listen for window resize to update mobile detection
        window.addEventListener('resize', this.handleResize);
        
        // Listen for browser back/forward navigation
        window.addEventListener('popstate', this.handlePopState);
        
        // Add animation delay for navigation items
        this.$nextTick(() => {
            this.animateNavItems();
        });
    },
    
    beforeUnmount() {
        // Clean up event listeners
        window.removeEventListener('resize', this.handleResize);
        window.removeEventListener('popstate', this.handlePopState);
    },
    watch: {
        activeNav: {
            immediate: true,
            handler(newNav) {
                // Use route-specific SEO settings based on active navigation
                const routeSeo = routeSeoSettings[newNav] || {};
                useHead(generateMetaInfo(routeSeo));
            }
        },
        hasError: {
            handler(newErrorState) {
                if (newErrorState) {
                    // Set appropriate SEO meta tags for error page
                    useHead(generateMetaInfo({
                        title: 'Page Not Found | Whatley Julitar',
                        description: 'The page you were looking for could not be found.',
                        robots: 'noindex, nofollow' // Don't index error pages
                    }));
                }
            }
        }
    },
    async created() {
        try {
            // Fetch both JSON data files concurrently
            const timestamp = new Date().getTime(); // Use timestamp to renew JSON
            const [profileResponse, settingsResponse, techResponse] = await Promise.all([
                fetch(`/json/profile.json?t=${timestamp}`),
                fetch(`/json/settings.json?t=${timestamp}`),
                fetch(`/json/tech.json?t=${timestamp}`)
            ]);

            // Check if the responses are valid
            if (!profileResponse.ok) throw new Error('Failed to fetch profile data');
            if (!settingsResponse.ok) throw new Error('Failed to fetch settings data');
            if (!techResponse.ok) throw new Error('Failed to fetch tech data');

            // Parse the JSON responses
            const [profileData, settingsData, techData] = await Promise.all([
                profileResponse.json(),
                settingsResponse.json(),
                techResponse.json()
            ]);

            // Update component data
            this.data = profileData;
            this.navItems = settingsData.navItems;
            this.tech = techData;
            this.dataLoaded = true;

            // Handle URL routing
            this.handleUrlRouting();
        } catch (error) {
            console.error('Error fetching JSON data:', error);
            this.hasError = true; // Set error state
        }

        // Set initial sidebar state based on actual sidebar state
        this.initialSidebarState = this.isSidebarCollapsed;
    },
    methods: {
        checkIfMobile() {
            // Check if window is available (for SSR compatibility)
            if (typeof window === 'undefined') return false;
            
            // Multiple mobile detection methods
            const userAgent = navigator.userAgent.toLowerCase();
            const mobileKeywords = ['mobile', 'android', 'iphone', 'ipad', 'ipod', 'blackberry', 'windows phone'];
            const isMobileUA = mobileKeywords.some(keyword => userAgent.includes(keyword));
            
            // Screen size check
            const isSmallScreen = window.innerWidth <= 768;
            
            // Touch device check
            const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
            
            return isMobileUA || (isSmallScreen && isTouchDevice);
        },
        
        handleResize() {
            // Update mobile detection on window resize
            this.isMobile = this.checkIfMobile();
        },
        
        updateSidebarState(isCollapsed) {
            this.isSidebarCollapsed = isCollapsed;
            // Save sidebar state to localStorage for persistence across refreshes
            localStorage.setItem('sidebarState', isCollapsed ? 'collapsed' : 'expanded');
        },
        logComponentData(component, data) {
            console.log(`${component} component received data:`, data);
            
            // Add special debugging for generalSkill data
            if (data && data.generalSkill) {
                console.log('generalSkill data details:', {
                    isDefined: !!data.generalSkill,
                    dataType: typeof data.generalSkill,
                    isArray: Array.isArray(data.generalSkill),
                    length: data.generalSkill ? data.generalSkill.length : 0,
                    content: data.generalSkill
                });
            }
        },
        
        handleUrlRouting() {
            try {
                // Parse URL parameters
                const urlParams = new URLSearchParams(window.location.search);
                const pageParam = urlParams.get('page');
                
                // Handle SEO Test access with special params
                if (pageParam === 'seotest' && urlParams.get('access') === 'whateyseo2025') {
                    this.setActiveNav('SEO Test');
                    return;
                }
                
                // Map URL parameter/path to navigation item
                const navMap = {
                    'about': 'About',
                    'history': 'Professional History',
                    'professional': 'Professional History', 
                    'professional-history': 'Professional History',
                    'playground': 'Playground',
                    'projects': 'Playground'
                };
                
                // Check if we have a valid path-based URL
                const pathname = window.location.pathname.toLowerCase();
                // Remove leading and trailing slashes for cleaner path matching
                const cleanPath = pathname.replace(/^\/|\/$/g, '');
                
                if (cleanPath && cleanPath !== "" && !cleanPath.includes('index.html')) {
                    // Check if this is a valid path we can route to
                    const pathParts = cleanPath.split('/');
                    const mainPath = pathParts[0]; // Get the first segment of the path
                    
                    // Try to match the path to a navigation item
                    const navItem = navMap[mainPath];
                    
                    if (navItem && this.navItems.some(item => item.name === navItem)) {
                        // Found a matching navigation item, update the active navigation
                        this.setActiveNav(navItem);
                        
                        // Update URL to canonical form without reloading the page
                        if (pathParts.length > 1 || window.location.search) {
                            window.history.replaceState({}, '', `/${mainPath}`);
                        }
                        return;
                    } else {
                        // Path isn't one we recognize - show error page
                        this.hasError = true;
                        this.errorType = 'not-found';
                        console.log(`Invalid path: ${pathname} - showing error page`);
                        return;
                    }
                }
                
                // Handle standard page navigation via query parameter if no path routing was applied
                if (pageParam) {
                    const navItem = navMap[pageParam.toLowerCase()];
                    
                    // If valid navigation exists, set it
                    if (navItem && this.navItems.some(item => item.name === navItem)) {
                        this.setActiveNav(navItem);
                    } else {
                        // Invalid URL parameter - show error page
                        this.hasError = true;
                        this.errorType = 'not-found';
                        console.log(`Invalid page parameter: ${pageParam} - showing error page`);
                    }
                }
            } catch (error) {
                console.error('Error in URL routing:', error);
                this.hasError = true;
                this.errorType = 'server-error';
            }
        },
        cleanupUrl() {
            // Remove all query parameters
            window.history.pushState({}, '', window.location.pathname);
        },
        toggleContacts() {
            this.showContacts = !this.showContacts;
        },
        setActiveNav(nav) {
            this.activeNav = nav;
            
            // Map navigation names to URL paths for cleaner URLs
            const pathMap = {
                'About': 'about',
                'Professional History': 'professional-history',
                'Playground': 'playground'
            };
            
            // Get the path associated with this navigation item
            const path = pathMap[nav];
            
            if (path) {
                // Update the URL to reflect the current navigation without reloading the page
                // This creates clean URLs like /playground, /about, /professional-history
                window.history.pushState({}, '', `/${path}`);
            } else if (nav === 'SEO Test') {
                // Special case for SEO Test - keep the access parameter
                const currentUrl = new URL(window.location.href);
                currentUrl.searchParams.set('page', 'seotest');
                currentUrl.searchParams.set('access', 'whateyseo2025');
                window.history.pushState({}, '', currentUrl.toString());
            } else {
                // For any other navigation items, clear the URL
                window.history.pushState({}, '', window.location.pathname);
            }
        },
        
        animateNavItems() {
            // Add staggered animation to navigation items
            const navItems = document.querySelectorAll('.navbar-item');
            navItems.forEach((item, index) => {
                item.style.animation = `slideInFromTop 0.6s ease-out ${index * 0.1}s both`;
            });
        },
        
        handlePopState() {
            // This method is called when the user navigates using browser back/forward buttons
            // We need to update the active navigation tab based on the new URL
            this.handleUrlRouting();
        },
        
        resetError() {
            this.hasError = false;
            this.errorType = 'not-found';
            this.setActiveNav('About');
        },
        closeModal() {
            this.showModal = false;
        }
    }
}
</script>

<style scoped>
@import '../../assets/css/styles.css';
@import '../../assets/css/responsive.css';
@import '../../assets/css/fixedSidebar.css';

/* Override imported CSS that causes gaps */
main {
    padding-top: 0 !important;
    margin-top: 0 !important;
}

/* Main content positioning without transitions */
.main-content {
    transition: none !important;
    position: relative !important;
    margin-top: 20px !important;
    padding-top: 0 !important;
}

.main-content.main-with-sidebar {
    margin-left: 0 !important;
    padding-left: 0 !important;
    width: 100% !important;
    max-width: 100% !important;
    transform: translateX(0) !important;
    overflow-x: hidden !important; /* Prevent horizontal scrolling */
}

.main-content.main-expanded {
    width: 100% !important;
    max-width: 100% !important;
    padding-left: 0 !important;
    padding-right: 20px !important; /* Add padding to prevent content touching edge */
    margin-left: 0 !important;
    transform: translateX(0) !important;
    overflow-x: hidden !important; /* Prevent horizontal scrolling */
}

/* Responsive adjustments for main layout */
@media (max-width: 580px) {
  main {
    display: flex;
    flex-direction: column;
    margin: 10px 5px;
    min-width: auto;
    padding: 0;
  }
  
  .main-content {
    width: 100%;
    min-width: 100%;
    padding: 0;
    margin-top: 20px;
  }
  
  .navbar-list {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
  }
  
  .navbar-item {
    margin: 5px;
  }
}

/* Enhanced navigation styling */
.navbar {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 8px 16px;
  margin-bottom: 16px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
}

.navbar::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
}

.navbar-list {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  list-style: none;
  padding: 0;
  margin: 0;
  gap: 6px;
}

.navbar-item {
  position: relative;
}

.navbar-link {
  position: relative;
  background: transparent;
  border: none;
  color: var(--light-gray);
  padding: 8px 14px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  text-transform: capitalize;
  letter-spacing: 0.5px;
}

.navbar-link::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
  border-radius: 8px;
}

.navbar-link::after {
  content: '';
  position: absolute;
  bottom: 2px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 2px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 1px;
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.navbar-link:hover {
  color: var(--white-1);
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
}

.navbar-link:hover::before {
  opacity: 0.1;
}

.navbar-link:hover::after {
  width: 70%;
}

.navbar-link.active {
  color: var(--white-1);
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%);
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.2);
  transform: translateY(-1px);
}

.navbar-link.active::before {
  opacity: 0.15;
}

.navbar-link.active::after {
  width: 80%;
}

/* Add glow effect for active state */
.navbar-link.active {
  position: relative;
}

.navbar-link.active::before {
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.2);
}

/* Responsive navigation improvements */
@media (min-width: 769px) {
  .navbar {
    padding: 10px 20px;
  }
  
  .navbar-list {
    gap: 8px;
  }
  
  .navbar-link {
    padding: 10px 16px;
    font-size: 0.95rem;
  }
}

@media (max-width: 768px) {
  .navbar {
    padding: 6px 12px;
    margin-bottom: 12px;
  }
  
  .navbar-list {
    justify-content: center;
    gap: 4px;
    flex-wrap: wrap;
  }
  
  .navbar-link {
    padding: 6px 12px;
    font-size: 0.85rem;
  }
}

/* Add subtle animation on load */
.navbar {
  animation: slideInFromTop 0.6s ease-out;
}

@keyframes slideInFromTop {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Improve click feedback - smooth and simple */
.navbar-link:active {
  transform: translateY(1px) scale(0.95);
  transition: all 0.1s ease;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.2);
}

/* Smooth transitions for all states */
.navbar-link {
  position: relative;
  z-index: 1;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}
</style>
